import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { WizardStepsComponent } from '@shared/components/wizard-steps/wizard-steps.component';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { NotaFiscalEntradaService } from '@shared/services/apis/orquestrador-compras/nota-fiscal-entrada.service';
import { MotorFiscalLookupService } from '@shared/services/apis/motor-fiscal/motor-fiscal-lookup.service';
import { ImportarXmlNFeEntradaRequest } from '@shared/services/requests/repo-dfe/importar-xml-nfe-entrada.request';
import { LookupItemStruct } from '@shared/services/structs/motor-fiscal/lookup-item.struct';
import { StatusValidacaoImportacaoXmlNFeEntradaEnum } from '@shared/enums/repo-dfe/status-validacao-importacao-xml-nfe-entrada.enum';
import { TipoOperacaoEnum } from '@shared/enums/motor-fiscal/tipo-operacao.enum';
import { OrigemImportacaoXmlNFeEntradaEnum } from '@shared/enums/suprimentos/origem-importacao-xml-nfe-entrada.enum';
import { ValidacaoImportacaoXmlNFeEntradaStruct } from '@shared/services/structs/repo-dfe/validacao-importacao-xml-nfe-entrada.struct';
import { RegraCorrespondenciaProdutoNFeEntradaEnum } from '@shared/enums/suprimentos/regra-correspondencia-produto-nfe-entrada.enum';
import { ORDEM_REGRAS_CORRESPONDENCIA_PADRAO } from '@shared/components/nota-fiscal-entrada-associacao-dialog/nota-fiscal-entrada-associacao-dialog-data';

import { ArquivoImportacao } from './arquivo-importacao';
import { FaseAssistenteEnum } from './fase-assistente.enum';
import { ImportarXmlNotaFiscalEntradaDialogResult } from './importar-xml-nota-fiscal-entrada-dialog-result';
import { ImportarXmlNotaFiscalEntradaDialogData } from './importar-xml-nota-fiscal-entrada-dialog-data';
import { REGRAS_LABELS, VALIDACOES_IMPORTACAO } from './importar-xml-nota-fiscal-entrada.constants';
import { RegraLinha } from './regra-linha';
import { StatusArquivoImportacaoEnum } from './status-arquivo-importacao.enum';
import { ValidacaoImportacaoLinha } from './validacao-importacao-linha';

import { ImportarXmlArquivoStepComponent } from './steps/importar-xml-arquivo-step/importar-xml-arquivo-step.component';
import { ImportarXmlValidacaoStepComponent } from './steps/importar-xml-validacao-step/importar-xml-validacao-step.component';
import { ImportarXmlNaturezaStepComponent } from './steps/importar-xml-natureza-step/importar-xml-natureza-step.component';
import { ImportarXmlRegrasStepComponent } from './steps/importar-xml-regras-step/importar-xml-regras-step.component';

export {
  ArquivoImportacao,
  FaseAssistenteEnum,
  ImportarXmlNotaFiscalEntradaDialogResult,
  RegraLinha,
  StatusArquivoImportacaoEnum,
  ValidacaoImportacaoLinha,
};

/**
 * Assistente de importação por XML organizado em steps.
 * A validação é temporária: o rascunho só é criado na ação final.
 */
@Component({
  selector: 'app-importar-xml-nota-fiscal-entrada-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatProgressSpinnerModule,
    WizardStepsComponent,
    AlertCardComponent,
    ImportarXmlArquivoStepComponent,
    ImportarXmlValidacaoStepComponent,
    ImportarXmlNaturezaStepComponent,
    ImportarXmlRegrasStepComponent,
  ],
  templateUrl: './importar-xml-nota-fiscal-entrada-dialog.component.html',
  styleUrls: ['./importar-xml-nota-fiscal-entrada-dialog.component.css'],
})
export class ImportarXmlNotaFiscalEntradaDialogComponent {
  readonly passos = ['Arquivo', 'Validação', 'Natureza de Operação', 'Regras'];
  readonly FaseAssistenteEnum = FaseAssistenteEnum;
  fase = FaseAssistenteEnum.Arquivo;
  arquivo: ArquivoImportacao | null = null;
  isProcessando = false;
  descricaoDocumento: string | null = null;
  validacoes: ValidacaoImportacaoLinha[] = this.criarValidacoesImportacao();
  descricaoNaturezaOperacaoXml: string | null = null;
  listaNaturezaOperacao: LookupItemStruct[] = [];
  idNaturezaOperacaoSistema: number | null = null;
  isSalvandoRascunho = false;
  idNotaExistente: number | null = null;
  isAssociacaoPendente = false;
  possuiDivergenciaBloqueante = false;

  readonly OrigemImportacaoXmlNFeEntradaEnum = OrigemImportacaoXmlNFeEntradaEnum;

  regras: RegraLinha[] = [...REGRAS_LABELS];
  ordemRegrasChaves: RegraCorrespondenciaProdutoNFeEntradaEnum[] = REGRAS_LABELS.map(r => r.key);
  consideraApenasDescricao = false;

  constructor(
    private dialogRef: MatDialogRef<ImportarXmlNotaFiscalEntradaDialogComponent, ImportarXmlNotaFiscalEntradaDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: ImportarXmlNotaFiscalEntradaDialogData,
    private alertService: AlertService,
    private notaFiscalEntradaService: NotaFiscalEntradaService,
    private motorFiscalLookupService: MotorFiscalLookupService,
  ) { }

  get passoAtual(): number {
    return this.fase === FaseAssistenteEnum.Arquivo ? 0 : this.fase === FaseAssistenteEnum.Validacao ? 1 : this.fase === FaseAssistenteEnum.Natureza ? 2 : 3;
  }

  get temArquivo(): boolean { return !!this.arquivo; }
  get isSucesso(): boolean { return this.arquivo?.status === StatusArquivoImportacaoEnum.Sucesso || this.arquivo?.status === StatusArquivoImportacaoEnum.Alerta; }
  get isErro(): boolean { return this.arquivo?.status === StatusArquivoImportacaoEnum.Erro; }
  get validacaoComErro(): ValidacaoImportacaoLinha | undefined {
    return this.validacoes.find(x => x.idStatus === StatusValidacaoImportacaoXmlNFeEntradaEnum.Erro);
  }

  onArquivoSelecionado(file: File): void {
    this.reiniciarImportacaoParaArquivo();
    this.arquivo = { file, nome: file.name, status: StatusArquivoImportacaoEnum.Pendente };
  }

  avancarParaValidacao(): void {
    if (!this.arquivo) return;
    this.fase = FaseAssistenteEnum.Validacao;
    this.processarArquivo();
  }

  voltarParaArquivo(): void { this.fase = FaseAssistenteEnum.Arquivo; }

  /** Atalho pelo indicador: retorno é livre; avanço respeita as mesmas pré-condições do assistente. */
  onPassoSelecionado(destino: number): void {
    if (destino === this.passoAtual || destino < 0 || destino >= this.passos.length) return;

    if (destino === 0) {
      this.voltarParaArquivo();
      return;
    }

    if (destino === 1) {
      if (!this.temArquivo) {
        this.alertService.show('Selecione um arquivo', 'Escolha um XML antes de acessar a validação.', AlertType.warning);
        return;
      }

      if (this.fase === FaseAssistenteEnum.Arquivo) this.avancarParaValidacao();
      else this.fase = FaseAssistenteEnum.Validacao;
      return;
    }

    // Validação em andamento: o indicador fica parado até ela terminar, sem aviso de espera.
    if (this.isProcessando) return;
    if (!this.isSucesso) {
      this.alertService.show('Validação pendente', 'Conclua a validação do XML antes de avançar.', AlertType.warning);
      return;
    }

    if (destino === 2) {
      if (this.fase === FaseAssistenteEnum.Validacao) this.avancarParaNatureza();
      else this.fase = FaseAssistenteEnum.Natureza;
      return;
    }

    if (!this.idNaturezaOperacaoSistema) {
      this.fase = FaseAssistenteEnum.Natureza;
      this.alertService.show('Natureza obrigatória', 'Selecione a natureza de entrada antes de configurar as regras.', AlertType.warning);
      return;
    }

    this.fase = FaseAssistenteEnum.Regras;
  }

  /** Uma nova seleção sempre invalida integralmente o resultado do XML anterior. */
  private reiniciarImportacaoParaArquivo(): void {
    this.fase = FaseAssistenteEnum.Arquivo;
    this.descricaoDocumento = null;
    this.descricaoNaturezaOperacaoXml = null;
    this.listaNaturezaOperacao = [];
    this.idNaturezaOperacaoSistema = null;
    this.validacoes = this.criarValidacoesImportacao();
    this.regras = [...REGRAS_LABELS];
    this.ordemRegrasChaves = REGRAS_LABELS.map(regra => regra.key);
    this.consideraApenasDescricao = false;
  }

  private async processarArquivo(): Promise<void> {
    if (!this.arquivo) return;
    this.isProcessando = true;
    this.arquivo.status = StatusArquivoImportacaoEnum.Validando;
    this.validacoes = this.criarValidacoesImportacao();
    try {
      const conteudoXmlBase64 = await this.lerArquivoComoBase64(this.arquivo.file);
      const request: ImportarXmlNFeEntradaRequest = { nomeArquivo: this.arquivo.nome, conteudoXmlBase64 };
      const response = await firstValueFrom(this.notaFiscalEntradaService.importarXml(request));
      this.descricaoDocumento = response?.descricaoDocumento ?? null;
      this.descricaoNaturezaOperacaoXml = response?.descricaoNaturezaOperacaoXml ?? null;
      this.idNotaExistente = response?.isNotaExistente ? response.idNFeEntrada : null;
      this.isAssociacaoPendente = response?.isAssociacaoPendente ?? false;
      this.possuiDivergenciaBloqueante = response?.possuiDivergenciaBloqueante ?? false;

      if (response?.isError) {
        this.arquivo.status = StatusArquivoImportacaoEnum.Erro;
        this.arquivo.mensagem = response.errorDescription;
        this.aplicarValidacoes(response?.validacoes ?? []);
        if (!this.validacaoComErro) this.marcarFalhaDeComunicacao(response.errorDescription);
      } else {
        this.aplicarValidacoes(response?.validacoes ?? []);
        if (response?.alertaAmbienteHomologacao) {
          this.arquivo.status = StatusArquivoImportacaoEnum.Alerta;
          this.arquivo.mensagem = response.alertaAmbienteHomologacao;
        } else {
          this.arquivo.status = StatusArquivoImportacaoEnum.Sucesso;
          this.arquivo.mensagem = 'Arquivo validado e pronto para continuar.';
        }
      }
    } catch {
      this.arquivo.status = StatusArquivoImportacaoEnum.Erro;
      this.arquivo.mensagem = 'Não foi possível ler e validar o arquivo. Tente novamente.';
      this.marcarFalhaDeComunicacao(this.arquivo.mensagem);
    }
    this.isProcessando = false;
  }

  private criarValidacoesImportacao(): ValidacaoImportacaoLinha[] {
    return VALIDACOES_IMPORTACAO.map(validacao => ({ ...validacao }));
  }

  private aplicarValidacoes(validacoes: ValidacaoImportacaoXmlNFeEntradaStruct[]): void {
    this.validacoes = this.validacoes.map(validacao => {
      const resultado = validacoes.find(x => x.idValidacao === validacao.idValidacao);
      return resultado ? { ...validacao, idStatus: resultado.idStatus, mensagem: resultado.mensagem } : validacao;
    });
  }

  private marcarFalhaDeComunicacao(mensagem: string): void {
    this.validacoes[0] = {
      ...this.validacoes[0],
      idStatus: StatusValidacaoImportacaoXmlNFeEntradaEnum.Erro,
      mensagem,
    };
  }

  private lerArquivoComoBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const resultado = reader.result as string;
        const base64 = resultado.split(',')[1] ?? '';
        resolve(base64);
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  // ------------------------------------------------------------------
  // Etapa 3 - Natureza
  // ------------------------------------------------------------------

  avancarParaNatureza(): void {
    this.fase = FaseAssistenteEnum.Natureza;
    this.carregarNatureza();
  }

  voltarParaValidacao(): void { this.fase = FaseAssistenteEnum.Validacao; }

  private carregarNatureza(): void {
    this.motorFiscalLookupService
      .getNaturezaOperacaoLookup(TipoOperacaoEnum.Entrada)
      .subscribe(lista => this.listaNaturezaOperacao = lista ?? []);
  }

  avancarParaRegras(): void {
    if (this.idNaturezaOperacaoSistema) this.fase = FaseAssistenteEnum.Regras;
  }

  // ------------------------------------------------------------------
  // Etapa 4 - Regras
  // ------------------------------------------------------------------

  voltarParaNatureza(): void { this.fase = FaseAssistenteEnum.Natureza; }

  moverRegra(indice: number, direcao: -1 | 1): void {
    const alvo = indice + direcao;
    if (alvo < 0 || alvo >= this.regras.length) return;
    [this.regras[indice], this.regras[alvo]] = [this.regras[alvo], this.regras[indice]];
    this.ordemRegrasChaves = this.regras.map(r => r.key);
  }

  restaurarRegrasPadrao(): void {
    this.regras = REGRAS_LABELS.filter(r => ORDEM_REGRAS_CORRESPONDENCIA_PADRAO.includes(r.key));
    this.consideraApenasDescricao = false;
    this.idNotaExistente = null;
    this.isAssociacaoPendente = false;
    this.possuiDivergenciaBloqueante = false;
    this.ordemRegrasChaves = this.regras.map(r => r.key);
  }

  async salvarRascunho(): Promise<void> {
    if (!this.arquivo || !this.idNaturezaOperacaoSistema) return;

    this.isSalvandoRascunho = true;
    try {
      const conteudoXmlBase64 = await this.lerArquivoComoBase64(this.arquivo.file);
      const request: ImportarXmlNFeEntradaRequest = {
        nomeArquivo: this.arquivo.nome,
        conteudoXmlBase64,
        isSalvarRascunho: true,
        idNaturezaOperacaoSistema: this.idNaturezaOperacaoSistema,
      };
      const response = await firstValueFrom(this.notaFiscalEntradaService.importarXml(request));
      if (response?.isError || !response?.idNFeEntrada) {
        this.alertService.show('Não foi possível iniciar o lançamento',
          'Tente novamente. O arquivo e as regras selecionadas foram preservados.', AlertType.error);
        return;
      }

      this.dialogRef.close({
        idNFeEntrada: response.idNFeEntrada,
        ordemRegras: this.ordemRegrasChaves,
        consideraApenasDescricao: this.consideraApenasDescricao,
      });
    } catch {
      this.alertService.show('Não foi possível iniciar o lançamento',
        'Tente novamente. O arquivo e as regras selecionadas foram preservados.', AlertType.error);
    } finally {
      this.isSalvandoRascunho = false;
    }
  }

  irParaNotaExistente(): void {
    if (!this.idNotaExistente) return;
    this.dialogRef.close({
      idNFeEntrada: this.idNotaExistente,
      ordemRegras: [],
      consideraApenasDescricao: false,
      acao: 'irParaNota',
    });
  }

  associarItensNotaExistente(): void {
    if (!this.idNotaExistente) return;
    this.dialogRef.close({
      idNFeEntrada: this.idNotaExistente,
      ordemRegras: [],
      consideraApenasDescricao: false,
      acao: 'associarItens',
    });
  }

  fechar(): void {
    this.dialogRef.close();
  }
}
